import { nanoid } from "nanoid/non-secure";
import { createPushStream, createScope } from "@proj-airi/pipelines-audio";
import CaptureWorkletURL from "./capture.worklet?worker&url";
//#region src/browser/sources.ts
const worklets = /* @__PURE__ */ new WeakMap();
function loadCaptureWorklet(context) {
	let ready = worklets.get(context);
	if (!ready) {
		ready = context.audioWorklet.addModule(CaptureWorkletURL);
		worklets.set(context, ready);
		ready.catch(() => worklets.delete(context));
	}
	return ready;
}
/**
* Copies a MediaStream's samples into `output` until `scope` closes.
*
* Track end closes the output. Context shutdown errors it. The graph is muted, because it only reads samples.
* Scope cleanup disconnects the nodes and never stops tracks. The caller decides who owns them.
*/
async function pipeMediaStream(context, stream, scope, output) {
	if (!stream.getAudioTracks().some((track) => track.readyState === "live")) throw new Error("Audio stream has no live track");
	await loadCaptureWorklet(context);
	scope.signal.throwIfAborted();
	const sourceId = nanoid();
	const node = context.createMediaStreamSource(stream);
	const worklet = new AudioWorkletNode(context, "airi-capture");
	const mute = context.createGain();
	mute.gain.value = 0;
	node.connect(worklet).connect(mute).connect(context.destination);
	scope.defer(() => {
		worklet.port.postMessage({ type: "close" });
		worklet.port.close();
		node.disconnect();
		worklet.disconnect();
		mute.disconnect();
	});
	worklet.port.onmessage = ({ data }) => {
		output.write({
			range: {
				sourceId,
				startFrame: data.startFrame,
				endFrame: data.startFrame + data.channels[0].length
			},
			sampleRate: data.sampleRate,
			channels: data.channels
		});
	};
	worklet.onprocessorerror = () => output.error(/* @__PURE__ */ new Error("Audio capture processor failed"));
	for (const track of stream.getAudioTracks()) track.addEventListener("ended", () => output.close(), {
		once: true,
		signal: scope.signal
	});
	context.addEventListener("statechange", () => {
		if (context.state === "closed") output.error(/* @__PURE__ */ new Error("Audio context closed during capture"));
	}, { signal: scope.signal });
}
/** Starts `connect` inside one connection scope and exposes its blocks. Cancelling the stream closes the scope. */
function openConnection(signal, connect) {
	const scope = createScope(signal);
	const output = createPushStream((reason) => void scope.close(reason));
	scope.defer(() => output.close());
	connect(scope, output).catch((error) => {
		output.error(error);
		scope.close(error);
	});
	return output.stream;
}
/** A borrowed MediaStream as a source. Tracks stay with their owner. */
function mediaStreamSource(stream, context) {
	return {
		live: true,
		open: (signal) => openConnection(signal, (scope, output) => pipeMediaStream(context, stream, scope, output))
	};
}
/**
* Opens the microphone for each connection.
*
* `open` calls getUserMedia and resumes its own AudioContext synchronously, so a click handler keeps the
* user gesture. Closing the connection stops the tracks, then closes the context.
*/
function microphoneSource(constraints, options = {}) {
	let current;
	return {
		live: true,
		get stream() {
			return current;
		},
		get echoCancellation() {
			return current?.getAudioTracks().some((track) => track.getSettings().echoCancellation === true) ?? false;
		},
		open: (signal) => openConnection(signal, async (scope, output) => {
			const context = new AudioContext(options.contextOptions);
			scope.defer(() => context.state === "closed" ? void 0 : context.close());
			const resumed = context.resume();
			resumed.catch(() => {});
			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			scope.defer(() => {
				stream.getTracks().forEach((track) => track.stop());
				if (current === stream) {
					current = void 0;
					options.onStream?.(void 0);
				}
			});
			scope.signal.throwIfAborted();
			await resumed;
			current = stream;
			options.onStream?.(stream);
			await pipeMediaStream(context, stream, scope, output);
		})
	};
}
//#endregion
export { mediaStreamSource, microphoneSource };
